/**
 * @file components/biz/listPage/FilterItem
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import React from 'react';
import {Input, Select, DatePicker, Radio, Search} from 'acud';
import moment from 'moment';
import _ from 'lodash';

import timeUtil from '../../../utils/timeUtil';
import {DU_PREFIX} from '../../_common/constants';
import {FilterControl} from './index';
import {useLocale} from '../../../components/locale';

import './index.less';

const CLS_PREFIX = `${DU_PREFIX}-filter-item`;
const RangePicker = DatePicker.RangePicker;
const Option = Select.Option;

const DEFAULT_WIDTH = 120;

export interface FilterItemProps {
    key: string;
    control: FilterControl;
    filterData: any;
    onItemChanged: (value: any, name: string, preventFilterAction?: boolean, forceFilterAction?: boolean) => void;
}

const FilterItem: React.FunctionComponent<FilterItemProps> = props => {
    const {control, filterData, onItemChanged} = props;
    const name = control.name;
    const value = filterData[name] || ''; // 默认值
    const [locale] = useLocale('HooksList');

    let controlItem: React.ReactElement | null = null;
    const width = control.width || DEFAULT_WIDTH; // 宽度
    if (control.type === 'textbox') {
        // zhangzhe 2019-04-16 输入框按回车就发起一次查询
        controlItem = (
            <Input
                style={{width}}
                value={value}
                onChange={evt => onItemChanged(evt.target.value, name, true)}
                onPressEnter={evt => onItemChanged(evt.currentTarget.value, name, false, true)}
                {...control.acudProps}
            />
        );
    }
    else if (control.type === 'search') {
        controlItem = (
            <Search
                style={{width}}
                value={value}
                onChange={evt => onItemChanged(evt.target.value, name, true)}
                onSearch={currentValue => onItemChanged(currentValue, name, false)}
                {...control.acudProps}
            />
        );
    }
    else if (control.type === 'select') {
        const renderOptions = (options: any[]) => options.map(option =>
            <Option key={option.value} value={option.value} title={option.text}>{option.text}</Option>);
        controlItem = (
            <Select
                style={{width}}
                value={value}
                mode={control.multi ? 'multiple' : ''}
                onChange={currentValue => onItemChanged(currentValue, name)}
                {...control.acudProps}
            >
                {renderOptions(control.options as any[])}
            </Select>
        );
    }
    else if (control.type === 'rangecalendar') {
        const disabledDate = (current: any) => {
            if (current && control.range
                && ((control.range.begin && timeUtil.toDate(current) < timeUtil.toDate(control.range.begin))
                || (control.range.end && timeUtil.toDate(current) > timeUtil.toDate(control.range.end)))) {
                return true;
            }
            return false;
        };
        // 格式
        const format = control.time ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

        const getRangePickerValue: (timeValue: any) => [moment.Moment, moment.Moment]
            = (timeValue: any): [any, any] => {
                const {begin, end} = timeValue;
                return [begin && moment(begin), end && moment(end)];
            };

        const getValue = (pickerValue: any) => {
            if (control.time) {
                // 这里默认返回utc格式
                return {
                    begin: timeUtil.timeToUtc(pickerValue[0]),
                    end: timeUtil.timeToUtc(pickerValue[1])
                };
            }
            return {
                begin: timeUtil.toDate(pickerValue[0]),
                end: timeUtil.toDate(pickerValue[1])
            };
        };
        if (control.time) {
            controlItem = (
                <RangePicker
                    style={{width: 370}}
                    showTime={{format: 'HH:mm:ss'}}
                    format={format}
                    placeholder={[locale?.startingTime, locale?.endTime]}
                    disabledDate={disabledDate}
                    value={getRangePickerValue(value)}
                    onChange={(__, dateStrings) => onItemChanged(getValue(dateStrings), name)}
                    {...control.acudProps}
                />
            );
        }
        else {
            controlItem = (
                <RangePicker
                    style={{width: 300}}
                    format={format}
                    placeholder={[locale?.startDate, locale?.endDate]}
                    disabledDate={disabledDate}
                    value={getRangePickerValue(value)}
                    onChange={(__, dateStrings) => onItemChanged(getValue(dateStrings), name)}
                    {...control.acudProps}
                />
            );
        }
    }
    else if (control.type === 'radio') {
        controlItem = (
            <Radio.Group
                style={{width: control.width}}
                value={value}
                onChange={e => onItemChanged((e.target as any).value, name)}
                {...control.acudProps}
            >
                {_.map(control.options, option => (
                    <Radio
                        key={option.value}
                        value={option.value}
                        checked={option.value}
                    >
                        {option.text || option.label}
                    </Radio>
                ))}
            </Radio.Group>
        );
    }

    return (
        <div className={`${CLS_PREFIX} ${CLS_PREFIX}-${name}`}>
            {control.label && (
                <label className={`${CLS_PREFIX}-label`}>{control.label}</label>
            )}
            {controlItem}
        </div>
    );
};

export default FilterItem;
